import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  addApprovalActionApi,
  addRecruitmentTrackerApi,
  createRecruitmentModuleOpenPositionApi,
  deleteRecruitmentModuleOpenPositionApi,
  deleteRecruitmentTrackerApi,
  getApprovalListActionApi,
  getRecruitmentModuleOpenPositionApi,
  getRecruitmentTrackerBulkUploadHistoryData,
  getRecruitmentTrackerListApi,
  getResignationUpdatesListApi,
  getSingleRecruitmentTrackerListApi,
  recruitmentTrackerBulkUploadApi,
  submitHikeReasonApi,
  sendFeedbackApprovalApi,
  updateHiringCheckListDataApi,
  updateRecruitmentModuleOpenPositionApi,
  updateRecruitmentTrackerListApi,
  getWeightedAverageReportApi,
  getRecruitmentTrackerStageCountApi,
  getDeprecatedRecruitmentTrackerListApi,
} from "@/apis/hrmsApis";

import type {
  AddApprovalPayloadType,
  AddOpenPositionPayloadType,
  AddRecruitmentTrackerPayloadType,
  ApprovalListPayloadType,
  DeleteOpenPositionPayloadType,
  GetHrmsBulkUploadHistoryPayloadType,
  GetOpenPositionPayloadType,
  GetRecruitmentTrackerPayloadType,
  GetResignationUpdatesListPayloadType,
  GetSingleRecruitmentTrackerPayloadType,
  RecruitmentTrackerBulkUploadType,
  SubmitHikeReasonPayload,
  SendFeedbackApprovalPayloadType,
  UpdateHiringCheckListPayloadType,
  UpdateOpenPositionPayloadType,
  GetWeightedAverageReportPayloadType,
} from "@/commonTypes/hrmsTypes";

export const createRecruitmentModuleOpenPositionAction = createAsyncThunk(
  "create/OpenPosition",
  async (data: AddOpenPositionPayloadType) => {
    const response = await createRecruitmentModuleOpenPositionApi(data);
    return response.data;
  }
);

export const updateRecruitmentModuleOpenPositionAction = createAsyncThunk(
  "update/OpenPosition",
  async (data: UpdateOpenPositionPayloadType) => {
    const response = await updateRecruitmentModuleOpenPositionApi(data);
    return response.data;
  }
);

export const getRecruitmentModuleOpenPositionAction = createAsyncThunk(
  "get/OpenPosition",
  async (data: GetOpenPositionPayloadType) => {
    const response = await getRecruitmentModuleOpenPositionApi(data);
    return response.data;
  }
);

export const deleteRecruitmentModuleOpenPositionAction = createAsyncThunk(
  "delete/OpenPosition",
  async (data: DeleteOpenPositionPayloadType) => {
    const response = await deleteRecruitmentModuleOpenPositionApi(data);
    return response.data;
  }
);

export const getRecruitmentTrackerListAction = createAsyncThunk(
  "get/recruiterTracker",
  async (data: GetRecruitmentTrackerPayloadType) => {
    const response = await getRecruitmentTrackerListApi(data);
    return response.data;
  }
);

export const getDeprecatedRecruitmentTrackerListAction = createAsyncThunk(
  "get/deprecatedRecruiterTracker",
  async (data: GetRecruitmentTrackerPayloadType) => {
    const response = await getDeprecatedRecruitmentTrackerListApi(data);
    return response.data;
  }
);

export const getRecruitmentTrackerStageCountAction = createAsyncThunk(
  "get/recruiterTrackerStageCount",
  async (data: GetRecruitmentTrackerPayloadType) => {
    const response = await getRecruitmentTrackerStageCountApi(data);
    return response.data;
  }
);

export const addRecruitmentTrackerAction = createAsyncThunk(
  "add/recruiterTracker",
  async (data: AddRecruitmentTrackerPayloadType) => {
    const response = await addRecruitmentTrackerApi(data);
    return response.data;
  }
);

export const updateRecruitmentTrackerListAction = createAsyncThunk(
  "update/recruiterTracker",
  async (data: AddRecruitmentTrackerPayloadType) => {
    const response = await updateRecruitmentTrackerListApi(data);
    return response.data;
  }
);

export const getSingleRecruitmentTrackerListAction = createAsyncThunk(
  "get/SingleRecruiterTracker",
  async (data: GetSingleRecruitmentTrackerPayloadType) => {
    const response = await getSingleRecruitmentTrackerListApi(data);
    return response.data;
  }
);
export const deleteRecruitmentTrackerAction = createAsyncThunk(
  "delete/recruiterTracker",
  async (data: DeleteOpenPositionPayloadType) => {
    const response = await deleteRecruitmentTrackerApi(data);
    return response.data;
  }
);

export const updateHiringCheckListDataAction = createAsyncThunk(
  "update/hiringCheckList",
  async (data: UpdateHiringCheckListPayloadType) => {
    const response = await updateHiringCheckListDataApi(data);
    return response.data;
  }
);

export const getApprovalListAction = createAsyncThunk(
  "get/approvalList",
  async (data: ApprovalListPayloadType) => {
    const response = await getApprovalListActionApi(data);
    return response.data;
  }
);

export const addApprovalAction = createAsyncThunk(
  "add/approval",
  async (data: AddApprovalPayloadType) => {
    const response = await addApprovalActionApi(data);
    return response.data;
  }
);
export const recruitementTrackerBulkUploadAction = createAsyncThunk(
  `bulkUpload/recruitmentTracker`,
  async (data: RecruitmentTrackerBulkUploadType) => {
    const response = await recruitmentTrackerBulkUploadApi(data);
    return response.data;
  }
);
export const getRecruitmentTrackerBulkUploadHistoryAction = createAsyncThunk(
  `bulkUploadHistory/recruitmentTracker`,
  async (data: GetHrmsBulkUploadHistoryPayloadType) => {
    const response = await getRecruitmentTrackerBulkUploadHistoryData(data);
    return response.data;
  }
);

/*
 *  we call `updateRecruitmentTrackerListApi` to update current salary in db
 * This is necessary because a user might submit a hike reason and not click the save button that is used to update details
 * we programmatically update the salary details then add the hike Reason.
 * after adding hike reason we refetch the record
 */

export const hikeReasonSubmitAction = createAsyncThunk(
  "submit/hikeReason",
  async (data: SubmitHikeReasonPayload, { dispatch }) => {
    const res = await updateRecruitmentTrackerListApi({
      id: data.hrms_id,
      token: data.token,
      candidateDetails: data.candidateDetails,
    } as unknown as AddRecruitmentTrackerPayloadType);

    if (!res?.data?.success) {
      return res.data;
    }

    const response = await submitHikeReasonApi(data);

    if (!response?.data?.success) {
      return response.data;
    }

    await dispatch(
      getSingleRecruitmentTrackerListAction({
        token: data.token,
        id: data.hrms_id,
        type: "hrms",
      })
    );
    return response.data;
  }
);

// resignation updates
export const getResignationUpdatesListAction = createAsyncThunk(
  "hrms/resignationList",
  async (data: GetResignationUpdatesListPayloadType) => {
    const response = await getResignationUpdatesListApi(data);
    return response.data;
  }
);

// send feedback approval
export const sendFeedbackApprovalAction = createAsyncThunk(
  "hrms/sendFeedbackApproval",
  async (data: SendFeedbackApprovalPayloadType) => {
    const response = await sendFeedbackApprovalApi(data);
    return response.data;
  }
);

export const getWeightedAverageReportAction = createAsyncThunk(
  "get/weightedAverageReport",
  async (data: GetWeightedAverageReportPayloadType) => {
    const response = await getWeightedAverageReportApi(data);
    return response.data;
  }
);
